import { useEffect, useState } from "react";
import api from "../api";
import { getImageUrl } from "../api";
import NoticiaDetalle from "./NoticiaDetalle";
import { motion } from "framer-motion"; // ℹ️ Para animaciones. Instalar con: npm install framer-motion

// Determina cuántos items mostrar según el ancho de la pantalla
const getPageSize = (width) => {
  if (width < 768) return 1;
  if (width < 1024) return 2;
  return 3;
};

const NoticiasCarousel = () => {
  const [publicaciones, setPublicaciones] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0); // Índice de inicio de la ventana
  const [selectedNoticia, setSelectedNoticia] = useState(null);
  const [pageSize, setPageSize] = useState(getPageSize(window.innerWidth));

  // --- EFECTOS SECUNDARIOS ---

  // 1. Ajustar el tamaño de página (pageSize) al cambiar el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => setPageSize(getPageSize(window.innerWidth));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 2. Resetear el índice al cambiar el número de items visibles
  useEffect(() => {
    setActiveIndex(0);
  }, [pageSize]);

  // 3. Cargar las publicaciones desde la API
  const fetchPublicaciones = async () => {
    try {
      const response = await api.get(`/publicaciones`);
      const data = response.data.data;
      const publicacionesConImagen = data.map((pub) => ({
        ...pub,
        imagen_url: getImageUrl(pub.imagen_url),
      }));
      // Debug: mostrar algunas URLs para validar
      if (publicacionesConImagen.length) {
        console.debug('[Carousel] imagen_url sample:', publicacionesConImagen.slice(0,3).map(p => p.imagen_url));
      }
      setPublicaciones(publicacionesConImagen);
    } catch (error) {
      console.error("Error al cargar publicaciones:", error);
    }
  };

  useEffect(() => {
    fetchPublicaciones(); // Carga inicial

    // 4. Refrescar publicaciones cada minuto
    const refreshInterval = setInterval(fetchPublicaciones, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // 5. Rotación automática del carrusel
  useEffect(() => {
    if (publicaciones.length > pageSize) {
      const rotationInterval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % publicaciones.length);
      }, 8000);
      return () => clearInterval(rotationInterval);
    }
  }, [publicaciones.length, pageSize]);

  // --- LÓGICA DE RENDERIZADO ---

  // Navegación manual
  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % publicaciones.length);
  const prevSlide = () => setActiveIndex((prev) => (prev === 0 ? publicaciones.length - 1 : prev - 1));

  // Si no hay publicaciones, mostrar mensaje
  if (!publicaciones.length) {
    return (
      <div className="w-full h-48 flex justify-center items-center text-gray-500">
        No hay publicaciones disponibles
      </div>
    );
  }

  // Calcular las publicaciones a mostrar en la ventana actual (efecto "sliding window")
  const currentPublicaciones = [];
  for (let i = 0; i < pageSize; i++) {
    if(publicaciones.length === 0) break;
    const index = (activeIndex + i) % publicaciones.length;
    currentPublicaciones.push(publicaciones[index]);
  }

  return (
    <>
      <div className="relative w-full">
        <motion.div
          key={activeIndex} // Forzar re-render y animación en cada cambio
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {currentPublicaciones.map((pub, index) => (
            <div
              key={`${pub.id_publicacion}-${index}`}
              className="relative h-64 rounded-xl overflow-hidden shadow-md bg-gray-200 cursor-pointer group"
              onClick={() => setSelectedNoticia(pub)}
            >
              <img
                src={pub.imagen_url || '/LogoPiedecuestana.png'}
                alt={pub.titulo}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = '/LogoPiedecuestana.png';
                  e.target.classList.add('object-contain');
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
                <h3 className="text-white p-4 text-lg font-semibold">
                  {pub.titulo}
                </h3>
              </div>
            </div>
          ))}
        </motion.div>

        {publicaciones.length > pageSize && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-[-1.5rem] top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-200 z-10"
            >
              ❮
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-[-1.5rem] top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-200 z-10"
            >
              ❯
            </button>
          </>
        )}

        {/* Los indicadores ahora son opcionales o podrían quitarse, ya que la rotación es continua */}
      </div>

      {selectedNoticia && (
        <NoticiaDetalle
          noticia={selectedNoticia}
          onClose={() => setSelectedNoticia(null)}
        />
      )}
    </>
  );
};

export default NoticiasCarousel;
